'use client';

import { useEffect, useRef, useState } from 'react';

import { useSetAtom } from 'jotai';

import { PAGE_TYPE } from '@core/Constants/PageType';
import { useElementObserver } from '@core/Hooks/UseElementObserver';
import { Typography } from '@gds/Typography/Typography';
import { dealerNumberErrorAtom } from '@pdp/Atoms/DealerNumberError.atom';
import { showDealerNumberPdpAtom } from '@pdp/Atoms/ShowDealerNumberPdp.atom';
import { LeadCTAs } from '@pdp/Components/LeadCTAs/LeadCTAs';
import { VehicleAttributes } from '@pdp/Components/VehicleAttributes/VehicleAttributes';
import { adminFeeMessagesAtom } from '@shared-components/AdminFee/AdminFee.atoms';

import { GuaranteeWrapper } from '../GuaranteeWrapper/GuaranteeWrapper';
import { SavedSearchCta } from '../SavedSearchCta/SavedSearchCta';
import type { StickyInfoProps } from './StickyInfo.entity';

import styles from './StickyInfo.module.css';

export const StickyInfo = ({
  strings: {
    pdpStrings,
    vehicleAttributesStrings,
    leadFormMessages,
    guaranteeStrings,
    priceRatingStrings,
    savedSearchMessages,
    adminFeeStrings,
    testDriveCtaStrs,
  },
  vehicle,
  hasCampaignDiscount,
  isStockUnavailable,
}: StickyInfoProps) => {
  const $stickyInfo = useRef<HTMLDivElement>(null);
  const hasStartedAutoGenerateNo = useRef(false);
  const [isSticky, setIsSticky] = useState(false);

  const setIsNumberVisible = useSetAtom(showDealerNumberPdpAtom);
  const setDealerNumberError = useSetAtom(dealerNumberErrorAtom);
  const setAdminFeeStrings = useSetAtom(adminFeeMessagesAtom);

  useEffect(() => {
    setIsNumberVisible(false);
    setDealerNumberError(false);
    setAdminFeeStrings(adminFeeStrings);
  }, []);

  // Observer for sticky info height
  useElementObserver($stickyInfo, entry => {
    const contentBoxSize = entry.contentBoxSize[0];
    const height = contentBoxSize.blockSize;
    const root = document.querySelector(':root') as HTMLElement;

    if (window.scrollY > 80 && window.innerWidth > 1023) {
      root.style.setProperty('--sticky-info-height', `${height}px`);
    } else {
      root.style.removeProperty('--sticky-info-height');
    }
  });

  // Scroll event for UspsItems condition
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 80 && window.innerWidth > 1023);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {['stickyInfo', 'stickyInfoCompact'].map(infoType => (
        <div
          ref={$stickyInfo}
          className={`${styles[infoType]} ${isSticky ? styles.sticky : ''}`}
          id="sticky-info"
          {...(infoType === 'stickyInfoCompact' && {
            'data-is-primary-nav-enabled': true,
          })}
          data-test-id={infoType}
          data-info-type={infoType}
          key={`in-${infoType}`}
        >
          <VehicleAttributes
            strings={vehicleAttributesStrings}
            vehicle={vehicle}
            pageType={PAGE_TYPE.Pdp}
            index={null}
            hasCampaignDiscount={hasCampaignDiscount}
            priceRatingStrings={priceRatingStrings}
            key={`va-${infoType}`}
            isStockUnavailable={isStockUnavailable}
          />
          {vehicle.spec?.guarantee && (
            <GuaranteeWrapper messages={guaranteeStrings} guarantee={vehicle.spec.guarantee} />
          )}

          {!isStockUnavailable ? (
            <LeadCTAs
              vehicle={vehicle}
              strings={{ pdpStrings, leadFormMessages, testDriveCtaStrs }}
              isEcom={!!vehicle.ecom.isEcom}
              hasInactiveStatus={vehicle.details.status === 'inactive'}
              isSticky={isSticky}
              key={`lcta-${infoType}`}
              hasStartedAutoGenerateNo={hasStartedAutoGenerateNo}
            />
          ) : (
            <>
              <Typography className={styles.stockUnavailable} variant={'subheading4'}>
                {pdpStrings.unavailableVehicleLabel}
              </Typography>
              <SavedSearchCta
                strings={savedSearchMessages}
                make={vehicle.make.id}
                model={vehicle.model.id}
              />
            </>
          )}
        </div>
      ))}
    </>
  );
};

StickyInfo.displayName = 'StickyInfo';
